/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import numelDimension from '@stdlib/ndarray/base/numel-dimension';
import getStride from '@stdlib/ndarray/base/stride';
import getOffset from '@stdlib/ndarray/base/offset';
import getData from '@stdlib/ndarray/base/data-buffer';
import { ndarray as strided } from './../../../../strided/snanmaxabs';


// MAIN //

/**
* Computes the maximum absolute value of a one-dimensional single-precision floating-point ndarray, ignoring `NaN` values.
*
* ## Notes
*
* -   The function expects the following ndarrays:
*
*     -   a one-dimensional input ndarray.
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing ndarrays
* @returns {number} maximum absolute value
*
* @example
* import Float32Vector from '@stdlib/ndarray/vector/float32';
*
* var x = new Float32Vector( [ 1.0, -2.0, NaN, 2.0 ] );
*
* var v = snanmaxabs( [ x ] );
* // returns 2.0
*/
function snanmaxabs( arrays ) {
	var x = arrays[ 0 ];
	return strided( numelDimension( x, 0 ), getData( x ), getStride( x, 0 ), getOffset( x ) ); // eslint-disable-line max-len
}


// EXPORTS //

export default snanmaxabs;
